import React, { useState, useEffect } from 'react';
import {
    Button,
    Menu,
    MenuItem,
    Box,
    Typography,
    Switch,
    FormControlLabel,
    Paper,
    Grid
} from '@mui/material';
import { useService } from 'contexts/ServiceContextProvider';
import { useWorkspaces } from 'contexts/WorkspacesContextProvider';
import { useTranslation } from 'react-i18next';
import { languages } from '../utils/LanguageImporter';
import "../css/views/SettingsPage.css";
import SkeletonSettingsPage from './Skeletons/SkeletonSettingsPage';
import NoWorkspacesModal from "../components/Modals/NoWorkspacesProfile";

/* eslint-disable react-hooks/exhaustive-deps */

const SettingsPage = () => {
    const { t, i18n } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [myUserPerms, setMyUserPerms] = useState(null);
    const [selectedValue, setSelectedValue] = useState('EN');
    const [selectedValue2, setSelectedValue2] = useState('English');
    const [loadingSettings, setLoadingSettings] = useState(true);
    const [noWorkspacesModalOpen, setNoWorkspacesModalOpen] = useState(false);
    const { getUserSettings, updateUserSetting, getStaffUserSettings, updateStaffUserSetting } = useService();
    const [isAdminOrOwner, setIsAdminOrOwner] = useState(false);
    const [isStaff, setIsStaff] = useState(false);

    const workspaceContext = useWorkspaces();
    const myWorkspaces = workspaceContext.workspaces;
    const workspaceLoading = workspaceContext.loading;

    const [notifications, setNotifications] = useState({
        task: true,
        workspace: true,
        project: true,
        event: true,
        vacation: true,
        mention: true,
        admin_vacation_request: true,
    });

    const [staffNotifications, setStaffNotifications] = useState({
        staff_tickets: true
    });

    useEffect(() => {
        document.title = t('titles.app_settings');
    }, []);

    useEffect(() => {
        if (!workspaceContext.loading && workspaceContext.workspaceWorkers.length > 0) {
            const userPerms = workspaceContext.getLoggedUserPermissions();
            setMyUserPerms(userPerms);
        }
    }, [workspaceContext.loading, workspaceContext.workspaceWorkers]);

    useEffect(() => {
        const fetchAllSettings = async () => {
            if (!myWorkspaces[0]?.id) {
                setNoWorkspacesModalOpen(true);
            } else {
                if (!myUserPerms) {
                    return;
                }
                try {
                    const isAdmin = myUserPerms.is_admin;
                    const isOwner = myUserPerms.is_owner;
                    const isStaff = myUserPerms.is_staff;

                    setIsAdminOrOwner(isAdmin || isOwner);
                    setIsStaff(isStaff);

                    // Fetch user settings based on the current workspace
                    const data = await getUserSettings(myWorkspaces[0]?.id);
                    setNotifications({
                        task: data.task_notifs,
                        project: data.project_notifs,
                        event: data.event_notifs,
                        vacation: data.vacation_notifs,
                        mention: data.mention_notifs,
                        admin_vacation_request: data.admin_vacation_request_notifs,
                    });

                    const staff_data = await getStaffUserSettings();
                    setStaffNotifications({
                        staff_tickets: staff_data.staff_tickets_notifs,
                    });
                } catch (error) {
                    console.error("Error fetching settings or permissions:", error);
                }
            }
            setLoadingSettings(false);
        };

        if (!workspaceLoading) {
            fetchAllSettings();
        }
    }, [myWorkspaces[0]?.id, workspaceLoading, myUserPerms]);

    useEffect(() => {
        const savedLanguage = localStorage.getItem('selectedLanguage');
        if (savedLanguage) {
            handleOptionClick(savedLanguage);
        } else if (i18n.language) {
            handleOptionClick(i18n.language.substring(0, 2).toUpperCase());
        }
    }, []);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleOptionClick = (language) => {
        i18n.changeLanguage(language.toLowerCase());
        const languageCode = languages[language] ? language : 'EN';
        const languageName = languages[languageCode] ? languages[languageCode] : 'English';
        setSelectedValue(languageCode);
        setSelectedValue2(languageName);
        localStorage.setItem('selectedLanguage', languageCode);
        localStorage.setItem('selectedLanguage2', languageName);
        handleClose();
    };

    const handleNoWorkspacesAcknowledge = () => {
        setNoWorkspacesModalOpen(false);
    };

    const handleToggleNotification = async (type) => {
        const updatedNotifications = {
            ...notifications,
            [type]: !notifications[type],
        };
        setNotifications(updatedNotifications);

        try {
            await updateUserSetting(`${type}_notifs`, updatedNotifications[type], myWorkspaces[0]?.id);
        } catch (error) {
            console.error("Error updating notification setting:", error);
        }
    };

    const handleToggleStaffNotification = async (type) => {
        const updatedStaffNotifications = {
            ...staffNotifications,
            [type]: !staffNotifications[type],
        };
        setStaffNotifications(updatedStaffNotifications);

        try {
            await updateStaffUserSetting(`${type}_notifs`, updatedStaffNotifications[type]);
        } catch (error) {
            console.error("Error updating notification setting:", error);
        }
    };

    const dropdownOptions = Object.entries(languages).filter(
        ([code]) => code !== selectedValue
    );

    return (
        <>
            {loadingSettings ? (
                <SkeletonSettingsPage />
            ) : (
                <Box className="main-box-padding-settings">
                    {/* GENERAL / UNIVERSAL SETTINGS */}
                    <Box className="editing-workspace-settings">
                        <Typography variant="h6" className="settings-text-settings">
                            {t("settings.application_settings")}
                        </Typography>
                    </Box>
                    <Paper className="paper-settings general-settings-paper" elevation={3}>
                        <Typography variant="h5" className="settings-section-header">
                            {t("settings.general_settings")}
                        </Typography>
                        <Grid container spacing={6}>
                            {/* Select Language (Always available) */}
                            <Grid item xs={6}>
                                <Typography variant="h6" className="settings-section-title">
                                    {t("settings.lng_select")}
                                </Typography>
                                <Button
                                    onClick={handleClick}
                                    variant="contained"
                                    className="select-language-button-settings"
                                >
                                    <Box>
                                        <Typography variant="subtitle1" className="selected-language-settings">
                                            {selectedValue}
                                        </Typography>
                                        <Typography variant="caption" className="selected-language-ext-settings">
                                            {selectedValue2}
                                        </Typography>
                                    </Box>
                                </Button>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleClose}
                                    className="language-selection-menu-settings"
                                >
                                    {dropdownOptions.map(([code, name]) => (
                                        <MenuItem
                                            key={code}
                                            onClick={() => handleOptionClick(code)}
                                            className="language-item-menu-settings"
                                        >
                                            <Box className="language-selection-menu-content-settings">
                                                <Typography variant="body2" className="selected-language-settings">
                                                    {code} - 
                                                </Typography>
                                                <Typography variant="body2" className="language-name-settings">
                                                    {name}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Grid>
                            {/* Staff notifications block (if applicable) */}
                            {isStaff && (
                                <Grid item xs={6}>
                                    <Typography variant="h6" className="settings-section-title">
                                        {t("settings.staff_notifications")}
                                    </Typography>
                                    <Box className="warnings-box-settings">
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={staffNotifications.staff_tickets}
                                                    onChange={() => handleToggleStaffNotification('staff_tickets')}
                                                />
                                            }
                                            label={t("settings.staff_tickets")}
                                        />
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>

                    <Box height={24} />

                    {/* WORKSPACE-DEPENDENT SETTINGS */}
                    {myWorkspaces[0]?.id ? (
                        <>
                            <Box className="editing-workspace-settings">
                                <Typography variant="h6" className="settings-text-settings">
                                    {t("settings.editing_settings")}
                                </Typography>
                                &nbsp;
                                <Typography variant="h6" className="workspace-name-settings">
                                    {myWorkspaces[0].name}
                                </Typography>
                            </Box>
                            <Paper className="paper-settings preferences-settings-paper" elevation={3}>
                                <Typography variant="h5" className="settings-section-header">
                                    {t("settings.preferences")}
                                </Typography>
                                <Grid container spacing={6}>
                                    {/* Email Notifications */}
                                    <Grid item xs={6}>
                                        <Typography variant="h6" className="settings-section-title">
                                            {t("settings.email_notifs")}
                                        </Typography>
                                        <Box className="warnings-box-settings">
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={notifications.task}
                                                        onChange={() => handleToggleNotification('task')}
                                                    />
                                                }
                                                label={t("settings.task")}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={notifications.project}
                                                        onChange={() => handleToggleNotification('project')}
                                                    />
                                                }
                                                label={t("settings.project")}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={notifications.event}
                                                        onChange={() => handleToggleNotification('event')}
                                                    />
                                                }
                                                label={t("settings.event")}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={notifications.vacation}
                                                        onChange={() => handleToggleNotification('vacation')}
                                                    />
                                                }
                                                label={t("settings.vacation")}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={notifications.mention}
                                                        onChange={() => handleToggleNotification('mention')}
                                                    />
                                                }
                                                label={t("settings.mention")}
                                            />
                                        </Box>
                                    </Grid>
                                    {isAdminOrOwner && (
                                        <Grid item xs={6}>
                                            <Typography variant="h6" className="settings-section-title">
                                                {t("settings.admin_email_notifs")}
                                            </Typography>
                                            <Box className="warnings-box-settings">
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={notifications.admin_vacation_request}
                                                            onChange={() => handleToggleNotification('admin_vacation_request')}
                                                        />
                                                    }
                                                    label={t("settings.vacation_requests")}
                                                />
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                            </Paper>
                        </>
                    ) : (
                        <Paper className="paper-settings preferences-settings-paper" elevation={3}>
                            <Typography variant="h6" className="settings-text-settings">
                                {t("workspace_settings.no_workspace_warning")}
                            </Typography>
                        </Paper>
                    )}
                </Box>
            )}
            <NoWorkspacesModal
                open={noWorkspacesModalOpen}
                onClose={handleNoWorkspacesAcknowledge}
                message={t("modal.no_workspace_message_settings")}
            />
        </>
    );
};

export default SettingsPage;
